import React from 'react';
import { 
  getAllConversions, 
  formatArea,
  UNIT_DISPLAY_NAMES 
} from '@/utils/areaConverter';
import { Ruler, Grid3X3 } from 'lucide-react';

/**
 * AreaDisplay - Display property area with multiple unit conversions
 * 
 * @param {Object} props
 * @param {number} props.sqftValue - Area in square feet (canonical)
 * @param {string} props.primaryUnit - Primary display unit
 * @param {number} props.primaryValue - Primary display value (optional, will calculate if not provided)
 * @param {boolean} props.showBreakdown - Show equivalent area breakdown
 * @param {number} props.maxBreakdownItems - Max breakdown items to show (default: 4)
 * @param {string} props.size - Display size: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} props.className - Additional classes
 */
const AreaDisplay = ({
  sqftValue,
  primaryUnit = 'sqft',
  primaryValue,
  showBreakdown = true,
  maxBreakdownItems = 4,
  size = 'md',
  className = ''
}) => {
  if (!sqftValue || sqftValue <= 0) {
    return (
      <div className={`text-gray-400 ${className}`}>
        Area not specified
      </div>
    );
  }
  
  const conversions = getAllConversions(sqftValue);
  const displayValue = primaryValue || conversions[primaryUnit] || sqftValue;
  const primaryLabel = UNIT_DISPLAY_NAMES[primaryUnit] || 'Sq Ft';
  
  // Get breakdown items (excluding primary unit)
  const priorityOrder = ['sqft', 'sqyd', 'guntas', 'acres', 'cents'];
  const breakdownItems = priorityOrder
    .filter(unit => unit !== primaryUnit && conversions[unit] > 0)
    .slice(0, maxBreakdownItems)
    .map(unit => ({
      unit,
      value: conversions[unit],
      label: UNIT_DISPLAY_NAMES[unit],
      formatted: formatArea(conversions[unit], unit)
    }));
  
  // Size styles
  const sizeStyles = {
    sm: {
      primary: 'text-lg font-semibold',
      label: 'text-xs',
      breakdown: 'text-xs',
      icon: 'w-4 h-4'
    },
    md: {
      primary: 'text-2xl font-bold',
      label: 'text-sm',
      breakdown: 'text-sm',
      icon: 'w-5 h-5'
    },
    lg: {
      primary: 'text-3xl font-bold',
      label: 'text-base',
      breakdown: 'text-base',
      icon: 'w-6 h-6'
    }
  };
  
  const styles = sizeStyles[size] || sizeStyles.md;
  
  return (
    <div className={`${className}`} data-testid="area-display">
      {/* Primary Area */}
      <div className="flex items-baseline gap-2">
        <span className={`${styles.primary} text-gray-900`} data-testid="area-primary-value">
          {displayValue >= 1000 
            ? displayValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })
            : displayValue.toFixed(2).replace(/\.?0+$/, '')
          }
        </span>
        <span className={`${styles.label} text-gray-600`} data-testid="area-primary-unit">
          {primaryLabel}
        </span>
      </div>
      
      {/* Area Breakdown */}
      {showBreakdown && breakdownItems.length > 0 && (
        <div className="mt-3" data-testid="area-breakdown">
          <div className="flex items-center gap-1.5 mb-2">
            <Grid3X3 className={`${styles.icon} text-gray-400`} />
            <span className={`${styles.breakdown} text-gray-500`}>Equivalent Area</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {breakdownItems.map(item => (
              <span 
                key={item.unit}
                className={`${styles.breakdown} text-gray-700`}
                data-testid={`area-breakdown-${item.unit}`}
              >
                {item.formatted}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * AreaBadge - Compact area display for cards and lists
 */
export const AreaBadge = ({ sqftValue, unit = 'sqft', className = '' }) => {
  if (!sqftValue || sqftValue <= 0) return null;
  
  const conversions = getAllConversions(sqftValue);
  const value = conversions[unit] || sqftValue;
  
  return (
    <span 
      className={`inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm text-gray-700 ${className}`}
      data-testid="area-badge"
    >
      <Ruler className="w-3 h-3" />
      {formatArea(value, unit)}
    </span>
  );
};

export default AreaDisplay;
